import React, { useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, AlertCircle, Download, Upload, CloudUpload, CloudDownload, Globe, Image as ImageIcon, Search, MessageSquare, Plus, Play, ExternalLink } from 'lucide-react';
import SettingsSwitch from '../ui/SettingsSwitch';
import SettingsSlider from '../ui/SettingsSlider';
import SettingsSelect from '../ui/SettingsSelect';
import { AVAILABLE_WIDGETS, PRESET_GROUP_TYPES, AVAILABLE_SHORTCUTS, getChannel } from '../../constants/presets';
import { getIconByName } from '../../constants/icons';
import '../../App.css';

const isVideoUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  const cleanUrl = url.split('?')[0].split('#')[0].toLowerCase();
  return /\.(mp4|webm|ogg|mov|m4v|flv|mkv)$/i.test(cleanUrl);
};

const SettingsContent = ({ activeTab, settings, updateSetting, setIsSettingsModalOpen, menuItems, pageData, setPageData, userInfo, setAuthToken, setUserInfo, isMaximized, customSearchEngines, setCustomSearchEngines, onResetAll }) => {
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [clearLocalData, setClearLocalData] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [showResetConfirmModal, setShowResetConfirmModal] = useState(false);
  
  const fileInputRef = React.useRef(null);
  
  const [feedbackContent, setFeedbackContent] = useState('');
  const [activeWallpaperCategory, setActiveWallpaperCategory] = useState('全部');
  const [wallpaperCategories, setWallpaperCategories] = useState(['全部', '默认', '风景', '动漫', '简约', '游戏', '科技', '我的图库']);
  const [presetWallpapers, setPresetWallpapers] = useState([]);
  const [visibleWallpaperCount, setVisibleWallpaperCount] = useState(30);
  const wallpaperGridRef = React.useRef(null);

  React.useEffect(() => {
    fetch('/api/v1/preset/sync?channel=' + encodeURIComponent(getChannel()))
      .then(res => res.json())
      .then(res => {
        if (res.code === 200 && res.data) {
          const dict = res.data;
          // Extract wallpaper categories dynamically from table
          const catList = Object.keys(dict)
            .filter(k => k.startsWith('WALLPAPER_CATEGORY:'))
            .map(k => JSON.parse(dict[k]))
            .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
          if (catList.length > 0) {
            setWallpaperCategories(['全部', ...catList.map(c => c.name), '我的图库']);
          }

          const wps = Object.keys(dict)
            .filter(k => k.startsWith('WALLPAPER:'))
            .map(k => JSON.parse(dict[k]))
            .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
          setPresetWallpapers(wps);
        }
      });
  }, []);
  
  const handleFeedbackSubmit = async () => {
    if (!userInfo) {
      alert('请先登录后再提交反馈');
      return;
    }
    if (!feedbackContent || feedbackContent.trim() === '' || feedbackContent === '<p><br></p>') {
      alert('请输入反馈内容');
      return;
    }
    try {
      const token = localStorage.getItem('tab365_token');
      const res = await fetch('http://localhost:8080/api/v1/feedback/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify({ content: feedbackContent })
      });
      const data = await res.json();
      if (data.code === 200) {
        alert('反馈提交成功，感谢您的建议！');
        setFeedbackContent('');
      } else {
        alert(data.msg || '提交失败');
      }
    } catch (e) {
      alert('网络错误，提交失败');
    }
  };

  const [customName, setCustomName] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  const [customIcon, setCustomIcon] = useState('');
  const customEngineFileInputRef = React.useRef(null);
  
  const handleCustomEngineUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 200 * 1024) {
        alert("图标文件太大，请选择 200KB 以下的图片！");
        return;
      }
      const reader = new FileReader();
      reader.onload = (ev) => {
        setCustomIcon(ev.target.result);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };
  
  const handleAddCustomEngine = () => {
    if (!customUrl.trim()) {
      alert("请输入搜索地址！");
      return;
    }
    let hostname = customName.trim();
    if (!hostname) {
      try { hostname = new URL(customUrl.trim()).hostname; } catch (e) { hostname = '自定义'; }
    }
    const newId = `custom_se_${Date.now()}`;
    const newEngine = {
      id: newId,
      name: hostname,
      url: customUrl.trim(),
      icon: customIcon || `https://api.iowen.cn/favicon/${hostname === '自定义' ? 'baidu.com' : hostname}.png`
    };
    const newCustom = [...(customSearchEngines || []), newEngine];
    if (setCustomSearchEngines) setCustomSearchEngines(newCustom);
    localStorage.setItem('tab365_customSearchEngines', JSON.stringify(newCustom));
    alert("添加成功！可在主页的搜索引擎列表查看到该平台。");
    setCustomName('');
    setCustomUrl('');
    setCustomIcon('');
  };

  const handleExportLocal = () => {
    const uid = localStorage.getItem('visitor_uid');
    const data = {
      uid,
      timestamp: Date.now(),
      data: {
        menuItems,
        pageData,
        settings
      }
    };
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    
    const now = new Date();
    const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
    a.download = `365Tab_Backup_${uid ? uid + '_' : ''}${dateStr}.json`;
    
    document.body.appendChild(a);
    a.click();
    
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    alert('本地备份文件已下载！');
  };

  const handleImportLocal = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = JSON.parse(e.target.result);
        if (parsed && parsed.data && parsed.data.menuItems && parsed.data.pageData && parsed.data.settings) {
          const uid = localStorage.getItem('visitor_uid');
          
          if (parsed.uid && uid && parsed.uid !== uid) {
            if (!window.confirm(`该备份文件属于另一个设备/浏览器 (UID: ${parsed.uid})。\n如果继续导入，当前工作区的所有配置将被该文件覆盖！\n\n是否确认继续导入？`)) {
              event.target.value = '';
              return;
            }
          }
          
          localStorage.setItem(`tab365_visitor_backup_${uid}`, JSON.stringify(parsed));
          alert('数据恢复成功！由于状态更新较多，即将刷新页面生效。');
          window.location.reload();
        } else {
          alert('无效的备份文件格式！');
        }
      } catch (err) {
        alert('读取备份文件失败，可能文件已损坏！');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleBackupCloud = async () => {
    const token = localStorage.getItem('tab365_token');
    if (!token) {
      alert('请先登录后再进行云端备份！');
      return;
    }
    
    const uid = localStorage.getItem('visitor_uid');
    const data = {
      uid,
      timestamp: Date.now(),
      data: {
        menuItems,
        pageData,
        settings
      }
    };
    
    try {
      const response = await fetch('/api/v1/sync/backup/upload', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });
      
      if (response.ok) {
        localStorage.setItem(`tab365_visitor_backup_${uid}`, JSON.stringify(data));
        alert('云端备份成功！');
      } else {
        alert('云端备份失败，请稍后重试！');
      }
    } catch (err) {
      alert('云端备份失败，网络错误！');
    }
  };

  const handleRestoreCloud = async () => {
    const token = localStorage.getItem('tab365_token');
    if (!token) {
      alert('请先登录后再进行云端恢复！');
      return;
    }
    
    if (!window.confirm('这将会覆盖您当前的本地数据，是否继续？')) {
      return;
    }
    
    try {
      const response = await fetch('/api/v1/sync/backup/latest', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const res = await response.json();
        if (res.code === 200 && res.data && res.data.data) {
          try {
            const parsedData = typeof res.data.data === 'string' ? JSON.parse(res.data.data) : res.data.data;
            const uid = localStorage.getItem('visitor_uid');
            localStorage.setItem(`tab365_visitor_backup_${uid}`, JSON.stringify(parsedData));
            alert('云端恢复成功！即将刷新页面生效。');
            window.location.reload();
          } catch(e) {
            alert('云端数据格式错误！');
          }
        } else {
          alert('云端未找到备份数据！');
        }
      } else {
        alert('云端恢复失败，请稍后重试！');
      }
    } catch (err) {
      alert('云端恢复失败，网络错误！');
    }
  };

  const renderTabContent = () => {
    switch(activeTab) {
      case 'icons':
        return (
          <div className="settings-panel-content">
            <h2 className="settings-panel-title">图标<span className="settings-panel-subtitle">图标样式、图标间距、宽度</span></h2>
            
            <div className="settings-card">
              <div className="settings-visual-options">
                <div 
                  className={`settings-visual-card ${settings.iconStyle === 'default' ? 'active' : ''}`}
                  onClick={() => updateSetting('iconStyle', 'default')}
                >
                  <div className="settings-visual-preview">
                    <div className="preview-icon-box" style={{ borderRadius: '18px', overflow: 'hidden' }}>
                      <img src="/logo_rounded.png" alt="icon" style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                    </div>
                  </div>
                  <span>默认</span>
                </div>
                <div 
                  className={`settings-visual-card ${settings.iconStyle === 'circle' ? 'active' : ''}`}
                  onClick={() => updateSetting('iconStyle', 'circle')}
                >
                  <div className="settings-visual-preview">
                    <div className="preview-icon-box" style={{ borderRadius: '50%', overflow: 'hidden' }}>
                      <img src="/logo_rounded.png" alt="icon" style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                    </div>
                  </div>
                  <span>圆形</span>
                </div>
              </div>

              <div className="settings-row-control">
                <span>图标大小</span>
                <SettingsSlider min={50} max={100} value={settings.iconSize} onChange={(v) => updateSetting('iconSize', v)} />
              </div>
              <div className="settings-row-control">
                <span>图标圆角</span>
                <SettingsSlider min={0} max={100} value={settings.iconRadius} onChange={(v) => updateSetting('iconRadius', v)} unit="%" />
              </div>
              <div className="settings-row-control">
                <span>不透明度</span>
                <SettingsSlider min={0} max={100} value={settings.iconOpacity} onChange={(v) => updateSetting('iconOpacity', v)} unit="%" />
              </div>
            </div>

            <div className="settings-card">
              <div className="settings-card-header">间距</div>
              <div className="settings-row-control">
                <span>图标间距</span>
                <SettingsSlider min={10} max={200} value={settings.iconDistance} onChange={(v) => updateSetting('iconDistance', v)} />
              </div>
            </div>

            <div className="settings-card">
              <div className="settings-card-header">背景</div>
              <div className="settings-row-control">
                <span>毛玻璃背景</span>
                <SettingsSwitch checked={settings.iconBackgroundVisible !== false} onChange={(v) => updateSetting('iconBackgroundVisible', v)} />
              </div>
            </div>

            <div className="settings-card">
              <div className="settings-card-header">名称</div>
              <div className="settings-row-control">
                <span>图标名称</span>
                <SettingsSwitch checked={settings.iconNameVisible} onChange={(v) => updateSetting('iconNameVisible', v)} />
              </div>
              {settings.iconNameVisible && (
                <div className="settings-row-control" style={{ marginTop: '12px' }}>
                  <span>名称颜色</span>
                  <div className="settings-color-picker" style={{ gap: '8px' }}>
                    {['#ffffff', '#10b981', '#06b6d4', '#8b5cf6', '#eab308', '#f97316', '#ef4444', 'rainbow'].map(color => (
                      <div 
                        key={color}
                        className={`color-dot ${settings.iconColor === color ? 'active' : ''} ${color === '#ffffff' ? 'light-bg' : ''}`}
                        style={{ background: color === 'rainbow' ? 'linear-gradient(45deg, #f00, #ff0, #0f0, #0ff, #00f, #f0f)' : color, width: '20px', height: '20px' }}
                        onClick={() => updateSetting('iconColor', color)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
            

            
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
  <div className="settings-danger-btn" style={{ padding: '8px 24px', width: 'fit-content' }} onClick={() => {
              updateSetting('iconStyle', 'default');
              updateSetting('iconSize', 72);
              updateSetting('iconRadius', 50);
              updateSetting('iconOpacity', 100);
              updateSetting('iconDistance', 30);
              updateSetting('iconNameVisible', true);
              updateSetting('iconBackgroundVisible', true);
              updateSetting('iconColor', '#ffffff');
            }}>
    恢复默认设置
  </div>
</div>
          </div>
        );
      case 'searchEngine':
        return (
          <div className="settings-panel-content">
            <h2 className="settings-panel-title">搜索引擎<span className="settings-panel-subtitle">搜索历史、猜你想搜、热搜排行</span></h2>
            
            <div className="settings-card">
              <div className="settings-card-header">搜索辅助功能</div>
              <div className="settings-row-control">
                <span>搜索历史</span>
                <SettingsSwitch checked={settings.enableSearchHistory !== false} onChange={(v) => updateSetting('enableSearchHistory', v)} />
              </div>
              <div className="settings-row-control">
                <span>猜你想搜</span>
                <SettingsSwitch checked={settings.enableGuessSearch !== false} onChange={(v) => updateSetting('enableGuessSearch', v)} />
              </div>
              <div className="settings-row-control">
                <span>热搜排行</span>
                <SettingsSwitch checked={settings.enableHotSearchRanking !== false} onChange={(v) => updateSetting('enableHotSearchRanking', v)} />
              </div>
              <div className="settings-row-control">
                <span>网站推荐</span>
                <SettingsSwitch checked={settings.enableSearchRecommend !== false} onChange={(v) => updateSetting('enableSearchRecommend', v)} />
              </div>
            </div>
            
            <div className="settings-card">
              <div className="settings-card-header">新增搜索平台</div>
              <div className="settings-row-control" style={{ padding: '16px' }}>
                <div style={{ display: 'flex', gap: '20px', width: '100%' }}>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                    <span style={{ whiteSpace: 'nowrap', marginRight: '16px', fontSize: '1rem', color: 'var(--text-secondary, rgba(255,255,255,0.7))' }}>平台名称</span>
                    <input 
                      type="text" 
                      placeholder="选填，例如：必应搜索"
                      value={customName}
                      onChange={e => setCustomName(e.target.value)}
                      style={{ flex: 1, background: 'var(--bg-hover-light, rgba(255,255,255,0.04))', border: '1px solid var(--border-color, rgba(255,255,255,0.1))', borderRadius: '6px', padding: '10px 12px', color: 'var(--text-color, #fff)', fontSize: '1rem', outline: 'none' }}
                      onFocus={(e) => { e.currentTarget.style.borderColor = '#3b82f6'; }}
                      onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--bg-hover, rgba(255,255,255,0.1))'; }}
                    />
                  </div>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                    <span style={{ whiteSpace: 'nowrap', marginRight: '16px', fontSize: '1rem', color: 'var(--text-secondary, rgba(255,255,255,0.7))' }}>搜索地址</span>
                    <input 
                      type="text" 
                      placeholder="必填，例如：https://cn.bing.com/search?q=%s"
                      value={customUrl}
                      onChange={e => setCustomUrl(e.target.value)}
                      style={{ flex: 1, background: 'var(--bg-hover-light, rgba(255,255,255,0.04))', border: '1px solid var(--border-color, rgba(255,255,255,0.1))', borderRadius: '6px', padding: '10px 12px', color: 'var(--text-color, #fff)', fontSize: '1rem', outline: 'none' }}
                      onFocus={(e) => { e.currentTarget.style.borderColor = '#3b82f6'; }}
                      onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--bg-hover, rgba(255,255,255,0.1))'; }}
                    />
                  </div>
                </div>
              </div>

              <div className="settings-row-control" style={{ padding: '0 16px 16px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ whiteSpace: 'nowrap', marginRight: '16px', fontSize: '1rem', color: 'var(--text-secondary, rgba(255,255,255,0.7))' }}>平台图标</span>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div 
                      style={{ 
                        width: '36px', height: '36px', borderRadius: '8px', 
                        background: 'var(--bg-hover-light, rgba(255,255,255,0.05))', border: '1px dashed rgba(255,255,255,0.2)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', overflow: 'hidden'
                      }}
                      onClick={() => customEngineFileInputRef.current?.click()}
                      title="上传图标 (200KB以内)"
                    >
                      {customIcon ? <img src={customIcon} alt="icon" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <ImageIcon size={16} color="var(--text-muted, rgba(255,255,255,0.4))" />}
                    </div>
                    <input type="file" accept="image/*" ref={customEngineFileInputRef} style={{ display: 'none' }} onChange={handleCustomEngineUpload} />
                  </div>
                </div>

                <div 
                  style={{ background: customUrl.trim() ? '#3b82f6' : 'var(--bg-hover, rgba(255,255,255,0.1))', color: customUrl.trim() ? '#fff' : 'var(--text-muted, rgba(255,255,255,0.3))', padding: '10px 24px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: customUrl.trim() ? 'pointer' : 'not-allowed', fontSize: '1rem', transition: 'all 0.2s' }}
                  onClick={() => customUrl.trim() && handleAddCustomEngine()}
                >
                  添加至搜索列表
                </div>
              </div>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
  <div className="settings-danger-btn" style={{ padding: '8px 24px', width: 'fit-content' }} onClick={() => {
              updateSetting('enableSearchHistory', true);
              updateSetting('enableGuessSearch', true);
              updateSetting('enableHotSearchRanking', true);
              updateSetting('enableSearchRecommend', true);
            }}>
    恢复默认设置
  </div>
</div>
          </div>
        );
      case 'theme':
        return (
          <div className="settings-panel-content">
            <h2 className="settings-panel-title">主题<span className="settings-panel-subtitle">深色模式、主题色</span></h2>
            
            <div className="settings-card">
              <div className="settings-visual-options">
                <div 
                  className={`settings-visual-card ${settings.themeMode === 'light' ? 'active' : ''}`}
                  onClick={() => updateSetting('themeMode', 'light')}
                >
                  <div className="settings-visual-preview light-preview">
                     <div className="preview-clock">02:44</div>
                     <div className="preview-grid"><div/><div/><div/><div/><div/><div/><div/><div/></div>
                  </div>
                  <span>浅色</span>
                </div>
                <div 
                  className={`settings-visual-card ${settings.themeMode === 'dark' ? 'active' : ''}`}
                  onClick={() => updateSetting('themeMode', 'dark')}
                >
                  <div className="settings-visual-preview dark-preview">
                     <div className="preview-clock">02:44</div>
                     <div className="preview-grid"><div/><div/><div/><div/><div/><div/><div/><div/></div>
                  </div>
                  <span>深色</span>
                </div>
              </div>

              <div className="settings-row-control" style={{ marginTop: '12px' }}>
                <span>跟随系统</span>
                <SettingsSwitch checked={settings.themeFollowSystem} onChange={(v) => updateSetting('themeFollowSystem', v)} />
              </div>
              
              <div className="settings-row-control" style={{ marginTop: '12px' }}>
                <span>主题色</span>
                <div className="settings-color-picker" style={{ gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {['#3b82f6', '#eab308', '#ef4444', '#10b981', '#06b6d4', '#8b5cf6', '#f59e0b', '#f97316', 'rainbow'].map(color => (
                    <div 
                      key={color}
                      className={`color-dot ${settings.themeColor === color ? 'active' : ''} ${color === '#ffffff' ? 'light-bg' : ''}`}
                      style={{ background: color === 'rainbow' ? 'linear-gradient(45deg, #f00, #ff0, #0f0, #0ff, #00f, #f0f)' : color }}
                      onClick={() => updateSetting('themeColor', color)}
                    />
                  ))}
                </div>
              </div>
            </div>


            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
              <div className="settings-danger-btn" style={{ padding: '8px 24px', width: 'fit-content' }} onClick={() => {
                updateSetting('themeMode', 'light');
                updateSetting('themeColor', 'blue');
              }}>
                恢复默认设置
              </div>
            </div>
          </div>
        );

      case 'wallpaper': {
        const filteredWallpapers = activeWallpaperCategory === '我的图库'
          ? (settings.myGallery || [])
          : presetWallpapers.filter(w => activeWallpaperCategory === '全部' || w.category === activeWallpaperCategory);
        const displayedWallpapers = filteredWallpapers.slice(0, visibleWallpaperCount);

        const handleWallpaperScroll = (e) => {
          const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
          if (scrollHeight - scrollTop - clientHeight < 80) {
            if (visibleWallpaperCount < filteredWallpapers.length) {
              setVisibleWallpaperCount(prev => Math.min(prev + 30, filteredWallpapers.length));
            }
          }
        };

        const handleCategoryClick = (cat) => {
          setActiveWallpaperCategory(cat);
          setVisibleWallpaperCount(30);
          if (wallpaperGridRef.current) {
            wallpaperGridRef.current.scrollTop = 0;
          }
        };

        return (
          <div className="settings-panel-content">
            <h2 className="settings-panel-title">壁纸<span className="settings-panel-subtitle">背景图片、遮罩、模糊</span></h2>
            
            <div className="settings-card">
              <div className="wallpaper-categories">
                {wallpaperCategories.map(cat => (
                  <div 
                    key={cat} 
                    className={`wallpaper-category ${activeWallpaperCategory === cat ? 'active' : ''}`}
                    onClick={() => handleCategoryClick(cat)}
                  >
                    {cat}
                  </div>
                ))}
              </div>
              
              <div 
                className="wallpaper-grid-wrapper"
                ref={wallpaperGridRef}
                onScroll={handleWallpaperScroll}
              >
                <div className="wallpaper-grid">
                  {displayedWallpapers.map((wp, idx) => (
                    <div key={wp.id || idx} className="wallpaper-thumbnail-container">
                      {isVideoUrl(wp.src) ? (
                        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                          <video 
                            src={wp.src} 
                            className="wallpaper-thumbnail" 
                            muted 
                            loop 
                            playsInline 
                            autoPlay
                            style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                          />
                          <div style={{ position: 'absolute', top: '4px', left: '4px', background: 'rgba(0,0,0,0.65)', color: '#fff', fontSize: '10px', padding: '1px 5px', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '3px', zIndex: 2 }}>
                            <Play size={10} /> 动态
                          </div>
                        </div>
                      ) : (
                        <img 
                          src={wp.thumbUrl || wp.thumb_url || wp.src} 
                          alt={`Wallpaper ${idx + 1}`} 
                          className="wallpaper-thumbnail" 
                          loading="lazy"
                          style={{ filter: wp.hue_rotate ? `hue-rotate(${wp.hue_rotate}deg)` : (wp.hueRotate ? `hue-rotate(${wp.hueRotate}deg)` : 'none') }}
                        />
                      )}
                      {(() => {
                        const isDarkDefault = (wp.category === '默认' && (wp.id === 'wp1791809881403' || wp.sort_order === 0)) || wp.src?.includes('photo-1451187580459-43490279c0fa');
                        const isLightDefault = (wp.category === '默认' && (wp.id === 'wp1791809881404' || wp.sort_order === 1)) || wp.src?.includes('photo-1497436072909-60f360e1d4b1');
                        if (isDarkDefault) {
                          return (
                            <div style={{
                              position: 'absolute',
                              top: '4px',
                              right: '4px',
                              background: 'rgba(15, 23, 42, 0.85)',
                              border: '1px solid rgba(96, 165, 250, 0.6)',
                              color: '#93c5fd',
                              fontSize: '10px',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              zIndex: 2,
                              backdropFilter: 'blur(4px)',
                              fontWeight: 500
                            }}>
                              深色主题默认
                            </div>
                          );
                        }
                        if (isLightDefault) {
                          return (
                            <div style={{
                              position: 'absolute',
                              top: '4px',
                              right: '4px',
                              background: 'rgba(15, 23, 42, 0.85)',
                              border: '1px solid rgba(96, 165, 250, 0.6)',
                              color: '#93c5fd',
                              fontSize: '10px',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              zIndex: 2,
                              backdropFilter: 'blur(4px)',
                              fontWeight: 500
                            }}>
                              浅色主题默认
                            </div>
                          );
                        }
                        return null;
                      })()}
                      <div className="wallpaper-thumbnail-overlay">
                        <div className="wallpaper-overlay-icons">
                          <div className="wallpaper-overlay-icon" data-title="应用" onClick={() => {
                            updateSetting('wallpaper', wp.src);
                            if (wp.hueRotate || wp.hue_rotate) updateSetting('wallpaperHue', wp.hueRotate || wp.hue_rotate);
                          }}>
                            <Check size={14} />
                          </div>
                          <div className="wallpaper-overlay-icon" data-title="打开" onClick={() => {
                            window.open(wp.src, '_blank');
                          }}>
                            <ExternalLink size={14} />
                          </div>
                          <div className="wallpaper-overlay-icon" data-title="下载壁纸" onClick={() => {
                            const originalSrc = wp.src;
                            const ext = isVideoUrl(originalSrc) ? 'mp4' : 'jpg';
                            const filename = `wallpaper_${idx + 1}.${ext}`;
                            const downloadUrl = `/api/v1/preset/wallpaper/download?url=${encodeURIComponent(originalSrc)}&name=${encodeURIComponent(filename)}`;
                            const a = document.createElement('a');
                            a.href = downloadUrl;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                          }}>
                            <Download size={14} />
                          </div>
                          <div className="wallpaper-overlay-icon" data-title="添加到图库" onClick={() => {
                            const gallery = settings.myGallery || [];
                            // Avoid exact duplicates
                            if (!gallery.find(g => g.src === wp.src && (g.hueRotate === wp.hueRotate || g.hueRotate === wp.hue_rotate))) {
                                updateSetting('myGallery', [...gallery, { src: wp.src, hueRotate: wp.hueRotate || wp.hue_rotate, id: 'gallery-' + Date.now() + Math.random() }]);
                            }
                          }}>
                            <Plus size={14} />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {visibleWallpaperCount < filteredWallpapers.length && (
                  <div style={{ textAlign: 'center', padding: '14px 0 6px', color: 'rgba(255, 255, 255, 0.45)', fontSize: '12px' }}>
                    向下滚动加载更多... (已显示 {displayedWallpapers.length} / {filteredWallpapers.length} 张)
                  </div>
                )}
                {filteredWallpapers.length > 30 && visibleWallpaperCount >= filteredWallpapers.length && (
                  <div style={{ textAlign: 'center', padding: '14px 0 6px', color: 'rgba(255, 255, 255, 0.3)', fontSize: '12px' }}>
                    已加载全部壁纸 (共 {filteredWallpapers.length} 张)
                  </div>
                )}
              </div>
            </div>

            <div className="settings-card">
              <div className="settings-row-control">
                <span>遮罩浓度</span>
                <SettingsSlider min={0} max={100} value={settings.wallpaperOpacity} onChange={(v) => updateSetting('wallpaperOpacity', v)} unit="%" />
              </div>
              <div className="settings-row-control" style={{ marginTop: '12px' }}>
                <span>模糊度</span>
                <SettingsSlider min={0} max={100} value={settings.wallpaperBlur} onChange={(v) => updateSetting('wallpaperBlur', v)} unit="%" />
              </div>
              <div className="settings-row-control" style={{ marginTop: '12px' }}>
                <span>自动壁纸</span>
                <SettingsSelect 
                  value={settings.autoWallpaper || 'none'} 
                  onChange={(v) => updateSetting('autoWallpaper', v)} 
                  options={[
                    { value: 'none', label: '不自动切换' },
                    { value: 'daily', label: '每日一图' },
                    { value: 'random', label: '每次打开随机' }
                  ]}
                />
              </div>
              <div className="settings-row-control" style={{ marginTop: '12px' }}>
                <span>桌面显示壁纸切换按钮</span>
                <SettingsSwitch checked={false} onChange={() => {}} />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
              <div className="settings-danger-btn" style={{ padding: '8px 24px', width: 'fit-content' }} onClick={() => {
                updateSetting('wallpaperOpacity', 0);
                updateSetting('wallpaperBlur', 0);
                updateSetting('autoWallpaper', 'none');
              }}>
                恢复默认设置
              </div>
            </div>
          </div>
        );
      }

      case 'sidebar':
        return (
          <div className="settings-panel-content">
            <h2 className="settings-panel-title">侧边栏<span className="settings-panel-subtitle">侧边栏位置、是否隐藏、宽度</span></h2>
            
            <div className="settings-card">
              <div className="settings-visual-options">
                <div 
                  className={`settings-visual-card sidebar-card ${settings.sidebarPosition === 'left' ? 'active' : ''}`}
                  onClick={() => updateSetting('sidebarPosition', 'left')}
                >
                  <div className="settings-visual-preview sidebar-preview left">
                     <div className="sidebar-col"><div/><div/><div/></div>
                  </div>
                  <div className="layout-badge">左侧</div>
                </div>
                <div 
                  className={`settings-visual-card sidebar-card ${settings.sidebarPosition === 'right' ? 'active' : ''}`}
                  onClick={() => updateSetting('sidebarPosition', 'right')}
                >
                  <div className="settings-visual-preview sidebar-preview right">
                     <div className="sidebar-col"><div/><div/><div/></div>
                  </div>
                  <div className="layout-badge">右侧</div>
                </div>
              </div>

              <div className="settings-row-control" style={{ marginTop: '20px' }}>
                <span>自动隐藏</span>
                <SettingsSwitch checked={settings.sidebarAutoHide} onChange={(v) => updateSetting('sidebarAutoHide', v)} />
              </div>

              <div className="settings-row-control">
                <span>透明度</span>
                <SettingsSlider min={0} max={100} value={settings.sidebarOpacity} onChange={(v) => updateSetting('sidebarOpacity', v)} unit="%" />
              </div>
              <div className="settings-row-control">
                <span>宽度</span>
                <SettingsSlider min={64} max={120} value={settings.sidebarWidth} onChange={(v) => updateSetting('sidebarWidth', v)} />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
  <div className="settings-danger-btn" style={{ padding: '8px 24px', width: 'fit-content' }} onClick={() => {
              updateSetting('sidebarPosition', 'right');
              updateSetting('sidebarAutoHide', false);
              updateSetting('sidebarOpacity', 10);
              updateSetting('sidebarWidth', 64);
            }}>
    恢复默认设置
  </div>
</div>
          </div>
        );
      case 'bottomMenu':
        return (
          <div className="settings-panel-content">
            <h2 className="settings-panel-title">底部栏<span className="settings-panel-subtitle">底部菜单显隐、推荐网站、最近点击</span></h2>
            
            <div className="settings-card">
              <div className="settings-row-control">
                <span>开启底部菜单</span>
                <SettingsSwitch checked={settings.bottomMenuVisible !== false} onChange={(v) => updateSetting('bottomMenuVisible', v)} />
              </div>
            </div>

            {settings.bottomMenuVisible !== false && (
              <>
                <div className="settings-card">
                  <div className="settings-card-header">推荐网站</div>
                  <div className="settings-row-control">
                    <span>显示推荐网站</span>
                    <SettingsSwitch checked={settings.bottomMenuRecommendVisible !== false} onChange={(v) => updateSetting('bottomMenuRecommendVisible', v)} />
                  </div>
                  {settings.bottomMenuRecommendVisible !== false && (
                    <div className="settings-row-control">
                      <span>最多显示数量</span>
                      <SettingsSlider min={1} max={10} value={settings.bottomMenuRecommendMax || 5} onChange={(v) => updateSetting('bottomMenuRecommendMax', v)} unit="个" />
                    </div>
                  )}
                </div>
                
                <div className="settings-card">
                  <div className="settings-card-header">最近点击</div>
                  <div className="settings-row-control">
                    <span>显示最近点击网站</span>
                    <SettingsSwitch checked={settings.bottomMenuRecentVisible !== false} onChange={(v) => updateSetting('bottomMenuRecentVisible', v)} />
                  </div>
                  {settings.bottomMenuRecentVisible !== false && (
                    <div className="settings-row-control">
                      <span>最多显示数量</span>
                      <SettingsSlider min={1} max={10} value={settings.bottomMenuRecentMax || 5} onChange={(v) => updateSetting('bottomMenuRecentMax', v)} unit="个" />
                    </div>
                  )}
                </div>
              </>
            )}
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
  <div className="settings-danger-btn" style={{ padding: '8px 24px', width: 'fit-content' }} onClick={() => {
              updateSetting('bottomMenuVisible', true);
              updateSetting('bottomMenuRecommendVisible', true);
              updateSetting('bottomMenuRecommendMax', 5);
              updateSetting('bottomMenuRecentVisible', true);
              updateSetting('bottomMenuRecentMax', 5);
            }}>
    恢复默认设置
  </div>
</div>
          </div>
        );
      case 'quickSettings':
        return (
          <div className="settings-panel-content">
            <h2 className="settings-panel-title">快捷设置<span className="settings-panel-subtitle">快捷设置位置、是否隐藏</span></h2>
            
            <div className="settings-card">
              <div className="settings-visual-options">
                <div 
                  className={`settings-visual-card sidebar-card ${settings.quickSettingsPosition === 'left' ? 'active' : ''}`}
                  onClick={() => updateSetting('quickSettingsPosition', 'left')}
                >
                  <div className="settings-visual-preview sidebar-preview left">
                     <div className="sidebar-col"><div/><div/><div/></div>
                  </div>
                  <div className="layout-badge">左侧</div>
                </div>
                <div 
                  className={`settings-visual-card sidebar-card ${settings.quickSettingsPosition !== 'left' ? 'active' : ''}`}
                  onClick={() => updateSetting('quickSettingsPosition', 'right')}
                >
                  <div className="settings-visual-preview sidebar-preview right">
                     <div className="sidebar-col"><div/><div/><div/></div>
                  </div>
                  <div className="layout-badge">右侧</div>
                </div>
              </div>

              <div className="settings-row-control" style={{ marginTop: '20px' }}>
                <span>切换主题按钮</span>
                <SettingsSwitch checked={settings.showThemeToggle !== false} onChange={(v) => updateSetting('showThemeToggle', v)} />
              </div>
              <div className="settings-row-control">
                <span>一键绿色按钮</span>
                <SettingsSwitch checked={settings.showCleanModeToggle !== false} onChange={(v) => updateSetting('showCleanModeToggle', v)} />
              </div>
              <div className="settings-row-control">
                <span>自动隐藏</span>
                <SettingsSwitch checked={settings.quickSettingsAutoHide} onChange={(v) => updateSetting('quickSettingsAutoHide', v)} />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
  <div className="settings-danger-btn" style={{ padding: '8px 24px', width: 'fit-content' }} onClick={() => {
              updateSetting('quickSettingsPosition', 'right');
              updateSetting('quickSettingsAutoHide', false);
              updateSetting('showThemeToggle', true);
              updateSetting('showCleanModeToggle', true);
            }}>
    恢复默认设置
  </div>
</div>
          </div>
        );
      case 'backup':
        return (
          <div className="settings-panel-content">
            <h2 className="settings-panel-title">备份与恢复<span className="settings-panel-subtitle">数据保存至云端，享受多设备同步</span></h2>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '32px', marginTop: '20px', padding: '0 4px' }}>
              
              <div>
                <div style={{ fontSize: '15px', fontWeight: 500, color: 'var(--text-color, rgba(255,255,255,0.9))', marginBottom: '6px' }}>备份到本地</div>
                <div style={{ fontSize: '13px', color: 'var(--text-muted, rgba(255,255,255,0.5))', marginBottom: '14px' }}>将设置、网格布局、搜索等数据保存为本地备份文件</div>
                <div className="settings-backup-primary" onClick={handleExportLocal}>
                  <Download size={14} />
                  备份到本地
                </div>
              </div>

              <div>
                <div style={{ fontSize: '15px', fontWeight: 500, color: 'var(--text-color, rgba(255,255,255,0.9))', marginBottom: '6px' }}>从本地恢复</div>
                <div style={{ fontSize: '13px', color: 'var(--text-muted, rgba(255,255,255,0.5))', marginBottom: '14px' }}>从本地备份文件恢复设置与数据</div>
                <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept=".json" onChange={handleImportLocal} />
                <div className="settings-backup-secondary" onClick={() => fileInputRef.current && fileInputRef.current.click()}>
                  <Upload size={16} />
                  从本地恢复
                </div>
              </div>

              <div>
                <div style={{ fontSize: '15px', fontWeight: 500, color: 'var(--text-color, rgba(255,255,255,0.9))', marginBottom: '6px' }}>备份到云端</div>
                <div style={{ fontSize: '13px', color: 'var(--text-muted, rgba(255,255,255,0.5))', marginBottom: '14px' }}>将当前设置与桌面数据保存为云端备份（需登录）</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div className="settings-backup-primary" onClick={handleBackupCloud}>
                    <CloudUpload size={16} />
                    备份到云端
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--text-muted, rgba(255,255,255,0.4))' }}>
                    上次备份: {(() => {
                      try {
                        const uid = localStorage.getItem('visitor_uid');
                        const data = localStorage.getItem(`tab365_visitor_backup_${uid}`);
                        if (data) {
                          const parsed = JSON.parse(data);
                          if (parsed.timestamp) {
                            return new Date(parsed.timestamp).toLocaleString('zh-CN');
                          }
                        }
                      } catch(e) {}
                      return '暂无';
                    })()}
                  </div>
                </div>
              </div>

              <div>
                <div style={{ fontSize: '15px', fontWeight: 500, color: 'var(--text-color, rgba(255,255,255,0.9))', marginBottom: '6px' }}>从云端恢复</div>
                <div style={{ fontSize: '13px', color: 'var(--text-muted, rgba(255,255,255,0.5))', marginBottom: '14px' }}>从最近一次云端备份恢复设置与数据（需登录）</div>
                <div className="settings-backup-secondary" onClick={handleRestoreCloud}>
                  <CloudDownload size={16} />
                  从云端恢复
                </div>
              </div>
              
              <div style={{ marginTop: '10px', borderTop: '1px solid var(--border-color, rgba(255,255,255,0.1))', paddingTop: '20px' }}>
                <div style={{ fontSize: '15px', fontWeight: 500, color: 'var(--text-color, rgba(255,255,255,0.9))', marginBottom: '6px' }}>本地与云端配置更新提示</div>
                <div style={{ fontSize: '13px', color: 'var(--text-muted, rgba(255,255,255,0.5))', marginBottom: '14px' }}>当本地与云端数据不一致时的处理方式</div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input 
                      type="radio" 
                      name="syncConflictPromptMode" 
                      value="always" 
                      checked={settings.syncConflictPromptMode === 'always' || !settings.syncConflictPromptMode} 
                      onChange={(e) => updateSetting('syncConflictPromptMode', e.target.value)} 
                    />
                    <span style={{ fontSize: '14px', color: 'var(--text-color, rgba(255,255,255,0.8))' }}>每次刷新时检测</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input 
                      type="radio" 
                      name="syncConflictPromptMode" 
                      value="on_login" 
                      checked={settings.syncConflictPromptMode === 'on_login'} 
                      onChange={(e) => updateSetting('syncConflictPromptMode', e.target.value)} 
                    />
                    <span style={{ fontSize: '14px', color: 'var(--text-color, rgba(255,255,255,0.8))' }}>登录成功后检测</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input 
                      type="radio" 
                      name="syncConflictPromptMode" 
                      value="disabled" 
                      checked={settings.syncConflictPromptMode === 'disabled'} 
                      onChange={(e) => updateSetting('syncConflictPromptMode', e.target.value)} 
                    />
                    <span style={{ fontSize: '14px', color: 'var(--text-color, rgba(255,255,255,0.8))' }}>不检测</span>
                  </label>
                </div>
              </div>
              
            </div>
          </div>
        );

      case 'account':
        return (
          <div className="settings-panel-content">
            <h2 className="settings-panel-title">个人信息<span className="settings-panel-subtitle">账号与数据管理</span></h2>
            
            <div className="settings-card" style={{ marginBottom: '20px', padding: '20px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '15px' }}>
                  <div style={{ color: 'var(--text-color, rgba(255,255,255,0.9))' }}><span style={{ color: 'var(--text-secondary, rgba(255,255,255,0.6))', marginRight: '8px' }}>昵称:</span> {userInfo ? (userInfo.nickname || `用户 ${userInfo.accountId || ''}`) : '未登录'}</div>
                  <div style={{ color: 'var(--text-secondary, rgba(255,255,255,0.7))', cursor: 'pointer', fontSize: '14px' }}>编辑</div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '15px' }}>
                  <div style={{ color: 'var(--text-color, rgba(255,255,255,0.9))' }}><span style={{ color: 'var(--text-secondary, rgba(255,255,255,0.6))', marginRight: '8px' }}>邮箱:</span> 未绑定</div>
                  <div style={{ color: 'var(--text-secondary, rgba(255,255,255,0.7))', cursor: 'pointer', fontSize: '14px' }}>绑定</div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '15px' }}>
                  <div style={{ color: 'var(--text-color, rgba(255,255,255,0.9))' }}><span style={{ color: 'var(--text-secondary, rgba(255,255,255,0.6))', marginRight: '8px' }}>手机:</span> 未绑定</div>
                  <div style={{ color: 'var(--text-secondary, rgba(255,255,255,0.7))', cursor: 'pointer', fontSize: '14px' }}>绑定</div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '15px' }}>
                  <div style={{ color: 'var(--text-color, rgba(255,255,255,0.9))' }}><span style={{ color: 'var(--text-secondary, rgba(255,255,255,0.6))', marginRight: '8px' }}>密码:</span> 未设置</div>
                  <div style={{ color: 'var(--text-secondary, rgba(255,255,255,0.7))', cursor: 'pointer', fontSize: '14px' }}>设置</div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '15px' }}>
                  <div style={{ color: 'var(--text-color, rgba(255,255,255,0.9))' }}><span style={{ color: 'var(--text-secondary, rgba(255,255,255,0.6))', marginRight: '8px' }}>微信:</span> 已绑定</div>
                  <div style={{ color: 'var(--text-secondary, rgba(255,255,255,0.7))', cursor: 'pointer', fontSize: '14px' }}>解绑</div>
                </div>
              </div>
            </div>

            <div className="settings-card" style={{ padding: '16px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'center' }} onClick={() => setIsLogoutModalOpen(true)}>
              <div style={{ color: '#ef4444', fontSize: '15px', fontWeight: 500 }}>退出登录</div>
            </div>

          </div>
        );
      case 'feedback':
        return (
          <div className="settings-panel-content">
            <h2 className="settings-panel-title">问题与建议<span className="settings-panel-subtitle">帮助我们做得更好</span></h2>
            <div className="settings-card">
              {!userInfo ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-secondary)' }}>
                  <MessageSquare size={48} style={{ opacity: 0.2, marginBottom: '16px' }} />
                  <div style={{ marginBottom: '16px' }}>提交问题与建议需要登录账户</div>
                  <button className="submit-btn" onClick={() => { setIsSettingsModalOpen(false); document.querySelector('.login-trigger-btn')?.click() }} style={{ width: 'fit-content', margin: '0 auto' }}>前往登录</button>
                </div>
              ) : (
                <div className="feedback-quill-container" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <ReactQuill 
                    theme="snow" 
                    value={feedbackContent} 
                    onChange={setFeedbackContent} 
                    placeholder="请详细描述您遇到的问题或您的建议..."
                    style={{ height: '200px', marginBottom: '40px', backgroundColor: 'var(--bg-color)', color: 'var(--text-color)' }}
                  />
                  <button className="submit-btn" onClick={handleFeedbackSubmit}>提交反馈</button>
                </div>
              )}
              {/* <textarea className="feedback-textarea" placeholder="请详细描述您遇到的问题或您的建议..."></textarea>
              ></textarea>
              <button className="submit-btn" style={{marginTop: '12px'}}>提交反馈</button> */}
            </div>
          </div>
        );
      case 'reset':
        return (
          <div className="settings-panel-content">
            <h2 className="settings-panel-title">重置设置<span className="settings-panel-subtitle">恢复到默认状态</span></h2>
            <div className="settings-card">
               <p style={{color: 'var(--text-secondary, #999)', fontSize: '0.9rem', marginBottom: '20px', lineHeight: '1.6'}}>此操作将清除所有自定义设置、桌面布局及本地缓存数据，并从服务端恢复初始默认配置（不会退出当前登录账号）。</p>
               <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
  <div 
    className="settings-danger-btn" 
    style={{ padding: '8px 24px', width: 'fit-content', cursor: isResetting ? 'wait' : 'pointer', opacity: isResetting ? 0.7 : 1 }}
    onClick={() => {
      if (!isResetting) {
        setShowResetConfirmModal(true);
      }
    }}
  >
    {isResetting ? '正在恢复中...' : '恢复默认设置'}
  </div>
</div>
            </div>

            {/* Theme-Adaptive Reset Confirm Modal */}
            {showResetConfirmModal && (
              <div 
                className="modal-overlay" 
                style={{ zIndex: 110000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} 
                onClick={() => { if (!isResetting) setShowResetConfirmModal(false); }}
              >
                <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="confirm-modal-close" onClick={() => { if (!isResetting) setShowResetConfirmModal(false); }}>
                    <X size={18} />
                  </div>
                  <div className="confirm-modal-header">
                    <AlertCircle size={20} color="#faad14" fill="#faad14" stroke="#1f1f1f" />
                    <span>恢复默认设置</span>
                  </div>
                  <div className="confirm-modal-body" style={{ textAlign: 'left', lineHeight: '1.6', fontSize: '14px' }}>
                    <div className="confirm-modal-title-text" style={{ fontWeight: 500, fontSize: '15px', marginBottom: '6px' }}>
                      确定要恢复默认设置吗？
                    </div>
                    <div className="confirm-modal-subtitle" style={{ fontSize: '13px' }}>
                      此操作将清除所有自定义设置、桌面布局及本地缓存数据，并从服务端恢复初始默认配置（不会退出当前登录账号）。
                    </div>
                  </div>
                  <div className="confirm-modal-footer" style={{ marginTop: '20px' }}>
                    <button 
                      className="confirm-btn-cancel" 
                      onClick={() => setShowResetConfirmModal(false)} 
                      disabled={isResetting}
                    >
                      取消
                    </button>
                    <button 
                      className="confirm-btn-ok" 
                      style={{ background: '#ff4d4f', borderColor: '#ff4d4f' }} 
                      disabled={isResetting}
                      onClick={async () => {
                        setIsResetting(true);
                        try {
                          if (onResetAll) {
                            await onResetAll();
                          }
                        } catch (e) {
                          alert('恢复默认设置失败，请重试');
                          setIsResetting(false);
                          setShowResetConfirmModal(false);
                        }
                      }}
                    >
                      {isResetting ? '正在恢复...' : '确定'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      case 'about':
        return (
          <div className="settings-panel-content">
            <h2 className="settings-panel-title">关于<span className="settings-panel-subtitle">版本信息</span></h2>
            <div className="settings-card" style={{display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 20px'}}>
               <img src="/logo.jpg" alt="logo" style={{width: 80, height: 80, borderRadius: '16px', marginBottom: '16px'}} />
               <h2 style={{margin: '0 0 8px 0'}}>365Tab</h2>
               <p style={{color: 'var(--text-secondary, #999)', margin: 0}}>Version 1.0.0</p>
               
               <div style={{display: 'flex', gap: '20px', marginTop: '40px', color: '#3b82f6', fontSize: '0.9rem', cursor: 'pointer'}}>
                  <span>用户协议</span>
                  <span>隐私政策</span>
                  <span>官方网站</span>
               </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <React.Fragment key={activeTab}>
        {renderTabContent()}
      </React.Fragment>
      <AnimatePresence>
        {isLogoutModalOpen && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 100000
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              style={{
                background: '#1a1a1a', borderRadius: '16px', width: 'auto', minWidth: '450px',
                border: '1px solid var(--border-color, rgba(255,255,255,0.1))', overflow: 'hidden',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
              }}
            >
              <div style={{ position: 'relative', padding: '24px 24px 16px 24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <AlertCircle size={20} color="#f59e0b" />
                  <span style={{ fontSize: '18px', fontWeight: 600, color: 'var(--text-color, #fff)' }}>退出登录？</span>
                </div>
                <X size={20} color="var(--text-muted, rgba(255,255,255,0.4))" style={{ cursor: 'pointer', position: 'absolute', right: '20px', top: '20px' }} onClick={() => setIsLogoutModalOpen(false)} />
              </div>
              <div style={{ padding: '0 24px 24px 24px' }}>
                <p style={{ color: 'var(--text-secondary, rgba(255,255,255,0.7))', fontSize: '15px', margin: '0 0 20px 0', lineHeight: '1.6', textAlign: 'center', whiteSpace: 'nowrap' }}>
                  退出登录将会失去数据备份、多端同步等功能，是否继续？
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', justifyContent: 'center' }} onClick={() => setClearLocalData(!clearLocalData)}>
                  <div style={{ 
                    width: '18px', height: '18px', borderRadius: '4px', 
                    border: `1px solid ${clearLocalData ? '#3b82f6' : 'var(--text-muted, rgba(255,255,255,0.3))'}`,
                    background: clearLocalData ? '#3b82f6' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.2s'
                  }}>
                    {clearLocalData && <Check size={14} color="var(--text-color, #fff)" />}
                  </div>
                  <span style={{ color: 'var(--text-color, #fff)', fontSize: '14px' }}>清除本地数据</span>
                </div>
              </div>
              <div style={{ padding: '20px 24px', display: 'flex', justifyContent: 'center', gap: '16px' }}>
                <div style={{ padding: '10px 32px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.2)', color: 'var(--text-color, #fff)', cursor: 'pointer', fontSize: '15px', fontWeight: 500 }} onClick={() => setIsLogoutModalOpen(false)}>
                  取消
                </div>
                <div style={{ padding: '10px 32px', borderRadius: '12px', background: '#3b82f6', color: 'var(--text-color, #fff)', cursor: 'pointer', fontSize: '15px', fontWeight: 500 }} onClick={() => {
                  localStorage.removeItem('tab365_token');
                  if (clearLocalData) {
                    const uid = localStorage.getItem('visitor_uid');
                    localStorage.removeItem(`tab365_visitor_backup_${uid}`);
                    localStorage.removeItem('tab365_settings');
                    window.location.reload();
                  } else {
                    if (setAuthToken) setAuthToken(null);
                    if (setUserInfo) setUserInfo(null);
                    setIsLogoutModalOpen(false);
                    if (setIsSettingsModalOpen) setIsSettingsModalOpen(false);
                  }
                }}>
                  确定
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SettingsContent;
